import { type ChangeEvent, type KeyboardEvent, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/atoms/Buttons';
import { useFormFieldControl } from '@/components/atoms/FormField';
import TextField from '@/components/atoms/TextField';

import { SecretInputStatus } from './SecretInputStatus';

import styles from './SecretInput.module.scss';

export type SecretInputValue =
    | { mode: 'keep' }
    | { mode: 'replace'; value: string }
    | { mode: 'clear' };

/** `inherited`: nothing of its own, but it takes the site's secret on save. */
export type SecretStatus = 'set' | 'notSet' | 'varies' | 'inherited';

/**
 * Shown over the empty box while a stored secret is kept: the same length whatever the secret
 * is, since the browser never holds it. Display only, never the input's value.
 */
const STORED_MASK = '\u2022'.repeat(8);

/**
 * A secret box for use inside a FormField, which gives it its label, error and id. An id of
 * its own must keep "password" out of it: password managers match on id and fill saved logins.
 */
export interface SecretInputProps {
    id?: string;
    status: SecretStatus;
    changedAt: Date | null;
    value: SecretInputValue;
    onChange: (value: SecretInputValue) => void;
    disabled?: boolean;
    /** A clear made elsewhere, the site's, removes this secret on save. */
    pendingClear?: boolean;
    /** The save needs it: aria-required on the box. */
    required?: boolean;
}

function SecretInput({
    id,
    status,
    changedAt,
    value,
    onChange,
    disabled = false,
    pendingClear = false,
    required = false,
}: SecretInputProps) {
    const { t } = useTranslation('atoms');
    // Inside a FormField the field's id ties its label to the box.
    const field = useFormFieldControl({ id });
    // useId's colons are valid in an id but not in a CSS selector, which jsdom and tests use.
    const generatedId = useId().replace(/:/g, '');
    const inputId = field.id ?? generatedId;
    const undoRef = useRef<HTMLButtonElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    // A clear disables the box and an undo removes its own button, so focus is moved on
    // rather than dropped: to Undo after a clear, back to the box after an undo.
    const focusAfter = useRef<'undo' | 'input' | null>(null);
    const [revealRequested, setRevealRequested] = useState(false);
    // Only a value typed in this session can be revealed; the reveal ends as soon as
    // the box is emptied or the form resets, so the next secret starts masked.
    if (revealRequested && value.mode !== 'replace') setRevealRequested(false);

    const typed = value.mode === 'replace' ? value.value : '';
    const isClearing = value.mode === 'clear';
    const isRevealed = revealRequested && value.mode === 'replace';
    const kept = value.mode === 'keep';
    const clearsElsewhere = pendingClear && kept;
    // Stored and untouched: the box looks filled, though the value stays with the server.
    const masked = (status === 'set' || status === 'inherited') && kept && !clearsElsewhere;
    // Only something stored here can be cleared here; an inherited one is the site's.
    const canClear = (status === 'set' || status === 'varies') && !isClearing && !clearsElsewhere;
    const statusId = `${inputId}-status`;

    useEffect(() => {
        if (focusAfter.current === 'undo' && isClearing) undoRef.current?.focus();
        else if (focusAfter.current === 'input' && !isClearing) inputRef.current?.focus();
        else return;
        focusAfter.current = null;
    });

    function handleChange(event: ChangeEvent<HTMLInputElement>): void {
        const next = event.target.value;
        onChange(next === '' ? { mode: 'keep' } : { mode: 'replace', value: next });
    }

    function handleClear(): void {
        setRevealRequested(false);
        focusAfter.current = 'undo';
        onChange({ mode: 'clear' });
    }

    function handleUndo(): void {
        focusAfter.current = 'input';
        onChange({ mode: 'keep' });
    }

    /** Deleting the dots deletes the value: the same as Clear, with the same Undo. */
    function handleKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
        if (!masked || !canClear || (event.key !== 'Backspace' && event.key !== 'Delete')) return;
        event.preventDefault();
        handleClear();
    }

    return (
        <div className={styles.secretInput}>
            <div className={styles.row}>
                <div className={styles.field}>
                    <TextField
                        ref={inputRef}
                        id={inputId}
                        type={isRevealed ? 'text' : 'password'}
                        // A saved portal login filled in here would be sent as the provider's
                        // secret. Browsers honour new-password; 1Password, LastPass and Bitwarden
                        // ignore autocomplete and only honour their own opt-out attributes.
                        autoComplete="new-password"
                        data-1p-ignore=""
                        data-lpignore="true"
                        data-bwignore=""
                        autoCapitalize="off"
                        autoCorrect="off"
                        spellCheck={false}
                        value={typed}
                        placeholder={
                            status === 'notSet' || isClearing || masked || clearsElsewhere
                                ? undefined
                                : t('secretInput.unchanged')
                        }
                        onChange={handleChange}
                        onKeyDown={handleKeyDown}
                        disabled={disabled || isClearing}
                        aria-required={required || undefined}
                        aria-describedby={statusId}
                    />
                    {masked && (
                        <span className={styles.mask} aria-hidden="true">
                            {STORED_MASK}
                        </span>
                    )}
                </div>
                <Button
                    variant="secondary"
                    pressed={isRevealed}
                    aria-controls={inputId}
                    onClick={() => setRevealRequested(!isRevealed)}
                    disabled={disabled || value.mode !== 'replace'}>
                    {t('secretInput.show')}
                </Button>
                {canClear && (
                    <Button variant="secondary" onClick={handleClear} disabled={disabled}>
                        {t('secretInput.clear')}
                    </Button>
                )}
            </div>
            <SecretInputStatus
                id={statusId}
                status={status}
                changedAt={changedAt}
                clearing={isClearing}
                pendingClear={clearsElsewhere}
                disabled={disabled}
                onUndo={handleUndo}
                undoRef={undoRef}
            />
        </div>
    );
}

export default SecretInput;
export { SecretInput };
